import { usePrevious } from "@src/global_functions/hooks";
import classNames from "classnames";
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Transition, TransitionGroup } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";

interface PageTransitionProps extends ComponentPropsWithoutRef<"div"> {
  readonly activeIndex: number;
  readonly pageContainerClassName?: string;
}

function getTranslate(state: TransitionStatus) {
  if (state === "entering") {
    return "calc(100% * var(--direction)) 0";
  }
  if (state === "exiting") {
    return "calc(-100% * var(--direction)) 0";
  }
  return 0;
}

/**
 * Component for transitioning left/right between a set of pages
 * Transitions content in the logical direction when active page increments (slides to the left) or decrements (slides to the right)
 *
 * Update the `activeIndex` and `children` props in tandem to switch pages
 */
function PageTransition({
  activeIndex,
  pageContainerClassName,
  className,
  children,
  ...containerProps
}: PageTransitionProps) {
  const [height, setHeight] = useState<number | string>(0);
  const [isNext, setIsNext] = useState(true);
  const prevIndex = usePrevious(activeIndex);
  useEffect(() => {
    if (prevIndex !== activeIndex) {
      setIsNext(Boolean(prevIndex === undefined || prevIndex < activeIndex));
    }
  }, [prevIndex, activeIndex]);

  const measureHeight = useCallback(
    (el: HTMLDivElement | null) => {
      if (el && el.dataset.index === String(activeIndex)) {
        setHeight(el.getBoundingClientRect().height);
      }
    },
    [activeIndex]
  );

  return (
    <TransitionGroup
      className={classNames("relative overflow-hidden", className)}
      style={
        {
          minHeight: height,
          "--direction": isNext ? 1 : -1,
        } as CSSProperties
      }
      {...containerProps}
    >
      <Transition
        key={activeIndex}
        timeout={{ exit: 500, enter: 0 }}
        onExited={() => setHeight("auto")}
      >
        {(state) => {
          const translate = getTranslate(state);
          return (
            <div
              className={classNames(
                "flex min-h-fit w-full",
                {
                  "transition-all duration-500":
                    state === "entered" || state === "exiting",
                  "absolute left-0 top-0 opacity-30": state !== "entered",
                  "translate-x-0 opacity-100": state === "entered",
                },
                pageContainerClassName
              )}
              style={{
                translate,
              }}
              ref={measureHeight}
              data-index={activeIndex}
            >
              {children}
            </div>
          );
        }}
      </Transition>
    </TransitionGroup>
  );
}

export default PageTransition;
